import { ReactNode, useEffect, useState } from 'react';
import { Spin } from 'antd';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { getCurrentLanguage } from '../../Helpers/RouteHelper';
import { PublicSiteService } from '../../Services/PublicSiteService';

type PublicSiteMemberAuthGuardProps = {
    children: ReactNode;
    /** When true, a logged-in family member is bounced to their own profile page - this route is
     *  for full members only (member directory, editable custom lists). */
    requireFullMember?: boolean;
};

export const PublicSiteMemberAuthGuard = ({ children, requireFullMember }: PublicSiteMemberAuthGuardProps) => {
    const params = useParams();
    const location = useLocation();
    const [checkingAuth, setCheckingAuth] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isFamilyMember, setIsFamilyMember] = useState(false);

    useEffect(() => {
        let isMounted = true;

        const validateAuth = async () => {
            try {
                const session = await PublicSiteService.GetSession();
                if (isMounted) {
                    setIsAuthenticated(true);
                    setIsFamilyMember(!!session.isFamilyMember);
                }
            } catch {
                if (isMounted) {
                    setIsAuthenticated(false);
                }
            } finally {
                if (isMounted) {
                    setCheckingAuth(false);
                }
            }
        };

        validateAuth();

        return () => {
            isMounted = false;
        };
    }, [params.companyurl]);

    if (checkingAuth) {
        return (
            <div style={{ textAlign: 'center', padding: '100px' }}>
                <Spin size='large' />
            </div>
        );
    }

    const language = getCurrentLanguage();
    const companyUrl = params.companyurl;

    if (!isAuthenticated) {
        if (!companyUrl) {
            return <Navigate to={`/${language}/`} replace />;
        }

        return (
            <Navigate
                to={`/${language}/${companyUrl}/login`}
                replace
                state={{ from: location.pathname + location.search }}
            />
        );
    }

    if (requireFullMember && isFamilyMember) {
        return <Navigate to={`/${language}/${companyUrl}/editmember`} replace />;
    }

    return <>{children}</>;
};
